import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Contact } from '../../../../../../types/api';
import { queryClient } from '../../../queries/queryClient';
import {
  deactivateAccountContactAPI,
  reactivateAccountContactAPI,
} from '../../../services/apis';
import noticeError from '../../../utils/errors/noticeError';
import { Tracker } from '../../../utils/tracker';

type Props = {
  contact: Contact;
  open: boolean;
  onClose: () => void;
};

export default function ContactDeactivateModal({
  contact,
  open,
  onClose,
}: Props) {
  const isDeactivating = contact.status !== 'Inactive';
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tracker = Tracker.getInstance();

  const handleClose = () => {
    if (isLoading) return;

    onClose();
  };

  const handleDeactivate = async () => {
    setIsLoading(true);
    try {
      if (isDeactivating) {
        await deactivateAccountContactAPI(contact.id);
      } else {
        await reactivateAccountContactAPI(contact.id);
      }
      queryClient.invalidateQueries('contacts');
      queryClient.invalidateQueries('activity');
      queryClient.invalidateQueries('application-access-contacts');
      toast.success('User updated successfully!');
      tracker.track(
        isDeactivating
          ? Tracker.EVENTS.USER_DEACTIVATED
          : Tracker.EVENTS.USER_REACTIVATED,
        { user_id: contact.id }
      );
      onClose();
    } catch (error) {
      console.error(error);
      noticeError(error);
      toast.error(error?.message ?? 'Something went wrong!');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: isMobile ? '90%' : 560,
          bgcolor: 'background.paper',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          px: isMobile ? 2 : 3.75,
          py: isMobile ? 2.5 : 3.75,
          borderRadius: 1.5,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h1">
            {isDeactivating ? 'Deactivate User' : 'Reactivate User'}
          </Typography>

          <IconButton onClick={handleClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body1" color="text.secondary" mb={3}>
          Are you sure you want to{' '}
          {isDeactivating ? 'deactivate' : 'reactivate'} this user?{' '}
          {isDeactivating
            ? 'Deactivation will remove this user from the account.'
            : 'Reactivation will add this user to the account.'}
        </Typography>

        <Box
          display="flex"
          justifyContent="flex-end"
          mt={3}
          gap={2}
          flexDirection={isMobile ? 'column-reverse' : 'row'}
        >
          <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            fullWidth={isMobile}
          >
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            type="button"
            onClick={handleDeactivate}
            disabled={isLoading}
            loading={isLoading}
            fullWidth={isMobile}
          >
            {isDeactivating ? 'Deactivate User' : 'Reactivate User'}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
}
